import api from './api';

const documentExportService = {
  /**
   * Get supported export formats
   */
  getSupportedFormats: async () => {
    try {
      const response = await api.get('/export/formats');
      return response.data;
    } catch (error) {
      console.error('Error getting supported formats:', error);
      throw error.response?.data?.detail || 'Failed to get supported formats';
    }
  },

  /**
   * Export conversation to DOCX
   */
  exportConversationToDOCX: async (conversationId, options = {}) => {
    try {
      const response = await api.post(`/export/conversations/${conversationId}/docx`, {
        title: options.title,
        include_diagrams: options.includeDiagrams !== false,
        format: 'docx'
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'conversation-export.docx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      console.error('Error exporting conversation to DOCX:', error);
      throw error.response?.data?.detail || 'Failed to export conversation to DOCX';
    }
  },

  /**
   * Export conversation to Google Docs
   */
  exportConversationToGoogleDocs: async (conversationId, options = {}) => {
    try {
      const response = await api.post(`/export/conversations/${conversationId}/google-docs`, {
        title: options.title,
        folder_id: options.folderId
      });
      
      return response.data;
    } catch (error) {
      console.error('Error exporting conversation to Google Docs:', error);
      throw error.response?.data?.detail || 'Failed to export conversation to Google Docs';
    }
  },

  /**
   * Export multiple project tasks to DOCX
   */
  exportProjectToDOCX: async (projectId, taskIds, options = {}) => {
    try {
      const response = await api.post(`/export/projects/${projectId}/docx`, {
        task_ids: taskIds,
        title: options.title,
        include_diagrams: options.includeDiagrams !== false,
        format: 'docx'
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `project-${projectId}-export.docx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    } catch (error) {
      console.error('Error exporting project to DOCX:', error);
      throw error.response?.data?.detail || 'Failed to export project to DOCX';
    }
  },

  /**
   * Export multiple project tasks to Google Docs
   */
  exportProjectToGoogleDocs: async (projectId, taskIds, options = {}) => {
    try {
      const response = await api.post(`/export/projects/${projectId}/google-docs`, {
        task_ids: taskIds,
        title: options.title,
        folder_id: options.folderId
      });
      
      return response.data;
    } catch (error) {
      console.error('Error exporting project to Google Docs:', error);
      throw error.response?.data?.detail || 'Failed to export project to Google Docs';
    }
  },

  /**
   * Authenticate with Google Workspace
   */
  authenticateGoogle: async (credentials) => {
    try {
      const response = await api.post('/export/google/authenticate', {
        credentials
      });
      
      return response.data;
    } catch (error) {
      console.error('Error authenticating with Google:', error);
      throw error.response?.data?.detail || 'Failed to authenticate with Google';
    }
  },

  /**
   * List Google Drive folders
   */
  listGoogleDriveFolders: async () => {
    try {
      const response = await api.get('/export/google/folders');
      return response.data;
    } catch (error) {
      console.error('Error listing Google Drive folders:', error);
      throw error.response?.data?.detail || 'Failed to list Google Drive folders';
    }
  },

  /**
   * Get sharing settings for Google Doc
   */
  getDocumentSharingSettings: async (documentId) => {
    try {
      const response = await api.get(`/export/google/documents/${documentId}/sharing`);
      return response.data;
    } catch (error) {
      console.error('Error getting document sharing settings:', error);
      throw error.response?.data?.detail || 'Failed to get sharing settings';
    }
  },

  /**
   * Update document permissions
   */
  updateDocumentPermissions: async (documentId, permissions) => {
    try {
      const response = await api.put(`/export/google/documents/${documentId}/permissions`, permissions);
      return response.data;
    } catch (error) {
      console.error('Error updating document permissions:', error);
      throw error.response?.data?.detail || 'Failed to update document permissions';
    }
  },

  // ── Unified Export System ──────────────────────────────────

  /**
   * Get supported export formats from unified export system
   */
  getUnifiedFormats: async () => {
    try {
      const response = await api.get('/export/formats');
      return response.data;
    } catch (error) {
      console.error('Error getting export formats:', error);
      throw error.response?.data?.detail || 'Failed to get export formats';
    }
  },

  /**
   * Check export system health (dependency availability)
   */
  getExportHealth: async () => {
    try {
      const response = await api.get('/export/health');
      return response.data;
    } catch (error) {
      console.error('Error checking export health:', error);
      throw error.response?.data?.detail || 'Failed to check export health';
    }
  },

  /**
   * Export conversation in any supported format
   * @param {string} format - Export format: docx, pdf, md, html, txt
   * @param {string} conversationId - Conversation ID
   * @param {object} options - Export options
   */
  exportConversation: async (format, conversationId, options = {}) => {
    try {
      const response = await api.post(
        `/export/${format}/conversations/${conversationId}`,
        {
          title: options.title,
          include_toc: options.includeToc !== false,
          include_cover: options.includeCover !== false,
          include_diagrams: options.includeDiagrams !== false,
          metadata: options.metadata
        },
        { responseType: 'blob' }
      );

      const contentDisposition = response.headers['content-disposition'];
      let filename = `export.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const warnings = response.headers['x-export-warnings'] || '';
      const fallback = response.headers['x-export-fallback'] === 'True';
      return { success: true, filename, warnings, fallback };
    } catch (error) {
      console.error(`Error exporting conversation to ${format}:`, error);
      throw error.response?.data?.detail || `Failed to export as ${format}`;
    }
  },

  /**
   * Export multiple project tasks in any supported format
   * @param {string} format - Export format: docx, pdf, md, html, txt
   * @param {string} projectId - Project ID
   * @param {string[]} taskIds - List of task IDs
   * @param {object} options - Export options
   */
  exportProjectTasks: async (format, projectId, taskIds, options = {}) => {
    try {
      const response = await api.post(
        `/export/${format}/projects/${projectId}/tasks`,
        {
          task_ids: taskIds,
          title: options.title,
          include_toc: options.includeToc !== false,
          include_cover: options.includeCover !== false,
          include_diagrams: options.includeDiagrams !== false,
          metadata: options.metadata
        },
        { responseType: 'blob' }
      );

      const contentDisposition = response.headers['content-disposition'];
      let filename = `project-export.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const warnings = response.headers['x-export-warnings'] || '';
      const fallback = response.headers['x-export-fallback'] === 'True';
      return { success: true, filename, warnings, fallback };
    } catch (error) {
      console.error(`Error exporting project tasks to ${format}:`, error);
      throw error.response?.data?.detail || `Failed to export as ${format}`;
    }
  },

  // ── Export Config (per-project branding) ──────────────────

  /**
   * Get export config for a project
   */
  getExportConfig: async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}/export-config`);
      return response.data;
    } catch (error) {
      console.error('Error getting export config:', error);
      throw error.response?.data?.detail || 'Failed to get export config';
    }
  },

  /**
   * Update export config for a project
   */
  updateExportConfig: async (projectId, config) => {
    try {
      const response = await api.put(`/projects/${projectId}/export-config`, config);
      return response.data;
    } catch (error) {
      console.error('Error updating export config:', error);
      throw error.response?.data?.detail || 'Failed to update export config';
    }
  },

  /**
   * Upload logo for export branding
   */
  uploadExportLogo: async (projectId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/projects/${projectId}/export-config/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error.response?.data?.detail || 'Failed to upload logo';
    }
  },

  /**
   * Upload letterhead for export branding
   */
  uploadExportLetterhead: async (projectId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(
        `/projects/${projectId}/export-config/letterhead`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading letterhead:', error);
      throw error.response?.data?.detail || 'Failed to upload letterhead';
    }
  },

  /**
   * Re-extract structured sections from a conversation message using LLM
   */
  reExtractSections: async (conversationId, messageTimestamp) => {
    try {
      const response = await api.post(
        `/conversations/${conversationId}/re-extract/${encodeURIComponent(messageTimestamp)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error re-extracting sections:', error);
      throw error.response?.data?.detail || 'Failed to re-extract sections';
    }
  },
};

export default documentExportService;